import { AppConfig } from "~/AppConfig";

import { extractAuthorizations } from "../helpers/authorizations";
import { AUTH_TOKEN_STORAGE_KEY } from "../helpers/consts";
import { calculateChecksum } from "../helpers/helpers";
import { API_PATH, apiClient } from "./ApiClient";

const baseUrl = AppConfig.API_URL + API_PATH;

export const uploadFile = async (
  file: File,
  collection?: string,
  reflection?: string,
) => {
  const authKey = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  const checksum = await calculateChecksum(file);
  const blobInfo = {
    collection,
    reflection,
    blob: {
      filename: file.name,
      content_type: file.type,
      byte_size: file.size,
      checksum: checksum,
    },
  };
  const response = await fetch(`${baseUrl}/uploads`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Token ${authKey}`,
      Accept: "application/json",
    },
    body: JSON.stringify(blobInfo),
  });

  if (!response.ok) {
    let detail: unknown;
    // try to get body
    try {
      detail = await response.clone().json();
    } catch {
      try {
        detail = await response.text();
      } catch {
        /* ignore */
      }
    }

    const suffix =
      detail == null
        ? ""
        : ` — ${typeof detail === "string" ? detail : JSON.stringify(detail)}`;

    throw new Error(`Upload init failed (${response.status})${suffix}`);
  }

  return response;
};

type UploadInitPayload = {
  direct_upload: {
    url: string;
    headers: Record<string, string>;
  };
  signed_id: string;
};

// Uploads the file directly to the storage and returns the blob's signed id
// to assign to the record (e.g. `logo`).
export async function uploadAndGetSignedId(
  file: File,
  collection: string | undefined = undefined,
  reflection: string | undefined = undefined,
): Promise<string> {
  const resp = await uploadFile(file, collection, reflection);
  const { direct_upload, signed_id } = (await resp.json()) as UploadInitPayload;

  if (!direct_upload?.url || !signed_id) {
    throw new Error("Upload init failed");
  }

  const putResponse = await fetch(direct_upload.url, {
    method: "PUT",
    headers: direct_upload.headers,
    body: file,
  });

  // Otherwise the record would point at a blob that never got its file.
  if (!putResponse.ok) {
    throw new Error(`Upload failed (${putResponse.status})`);
  }

  return signed_id;
}

export const getPlanUsageData = async () => {
  const planUsage = await apiClient.teams.usageList();

  return planUsage?.data?.team_usage;
};
export const getRegionsAndLanguages = async () => {
  const languages = await apiClient.sites.languagesList();
  const regions = await apiClient.sites.regionsList();
  const planUsage = await apiClient.teams.usageList();
  // Unlimited caps are serialized as null by the backend (usage-billed
  // plans); the generated type says number, so widen it here.
  const pageUsageMax: number | null =
    planUsage?.data?.team_usage?.team_limits.page_max ?? null;

  return {
    languages: languages?.data.languages || {},
    regions: regions?.data.regions || {},
    pageUsageMax,
  };
};

export const getPageMetas = async (
  siteId: number,
  pageId: number,
  kind: "titles" | "descriptions" | "schemas" | "summary",
) => {
  const response = await apiClient.sites.pagesPageMetasDetail(
    siteId,
    pageId,
    kind,
    {
      headers: {
        "Authorize-Member-Actions": "generate_customization",
      },
    },
  );
  const auth = extractAuthorizations(response.data);
  const memberAuths = auth?.member || [];

  return {
    page_metas: response.data?.page_metas,
    authorizations: memberAuths,
  };
};

export const applyPageMeta = async (
  siteId: number,
  pageId: number,
  pageMetaId: number,
) => {
  const response =
    await apiClient.sites.pagesPageMetasGenerateCustomizationCreate(
      siteId,
      pageId,
      pageMetaId,
    );

  return response.data;
};

export const generateTitleCustomization = async (
  siteId: number,
  pageId: number,
  id: number,
) => {
  const headers = {
    "Authorize-Collection-Actions": "create",
  };

  const response =
    await apiClient.sites.pagesWidgetCustomizationsGenerateTitleCreate(
      siteId,
      pageId,
      id,
      {
        headers,
      },
    );

  return response.data;
};

export const generateDescriptionCustomization = async (
  siteId: number,
  pageId: number,
  id: number,
) => {
  const headers = {
    "Authorize-Collection-Actions": "create",
  };

  const response =
    await apiClient.sites.pagesWidgetCustomizationsGenerateDescriptionCreate(
      siteId,
      pageId,
      id,
      {
        headers,
      },
    );

  return response.data;
};

export const generateSchemaCustomization = async (
  siteId: number,
  pageId: number,
  id: number,
) => {
  const headers = {
    "Authorize-Collection-Actions": "create",
  };

  const response =
    await apiClient.sites.pagesWidgetCustomizationsGenerateSchemaCreate(
      siteId,
      pageId,
      id,
      {
        headers,
      },
    );

  return response.data;
};

export const generateAiInstructionsCustomization = async (
  siteId: number,
  pageId: number,
  id: number,
) => {
  const headers = {
    "Authorize-Collection-Actions": "create",
  };

  const response =
    await apiClient.sites.pagesWidgetCustomizationsGenerateAiInstructionsCreate(
      siteId,
      pageId,
      id,
      {
        headers,
      },
    );

  return response.data;
};
